import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  CalendarClock, 
  Plus, 
  Settings, 
  Trash2, 
  Pause, 
  Play,
  DollarSign,
  Clock,
  AlertCircle,
  CheckCircle,
  Loader2,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AutoContributionManager({ clubId, clubName, clubAmount, clubFrequency }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [setupDialog, setSetupDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // Setup form state
  const [useClubFrequency, setUseClubFrequency] = useState(true);
  const [customAmount, setCustomAmount] = useState('');
  const [customFrequency, setCustomFrequency] = useState('weekly');
  const [customDay, setCustomDay] = useState('');

  useEffect(() => {
    loadSchedules();
  }, []);

  const loadSchedules = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auto-contributions`, {
        withCredentials: true
      });
      setSchedules(response.data.schedules || []);
    } catch (error) {
      console.error('Load schedules error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auto-contributions/setup`,
        {
          club_id: clubId,
          amount: useClubFrequency ? null : parseFloat(customAmount) || null,
          frequency: useClubFrequency ? null : customFrequency,
          custom_day: customDay || null,
          use_club_frequency: useClubFrequency
        },
        { withCredentials: true }
      );

      toast.success(response.data.message);
      setSetupDialog(false);
      loadSchedules();
      
      // Reset form
      setUseClubFrequency(true);
      setCustomAmount('');
      setCustomFrequency('weekly');
      setCustomDay('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup auto-contribution');
    } finally {
      setProcessing(false);
    }
  };

  const handleToggle = async (scheduleId, isActive) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/auto-contributions/${scheduleId}`,
        { is_active: !isActive },
        { withCredentials: true }
      );
      
      toast.success(isActive ? 'Auto-contribution paused' : 'Auto-contribution resumed');
      loadSchedules();
    } catch (error) {
      toast.error('Failed to update schedule');
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!confirm('Are you sure you want to cancel this auto-contribution?')) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/auto-contributions/${scheduleId}`,
        { withCredentials: true }
      );
      
      toast.success('Auto-contribution cancelled');
      loadSchedules();
    } catch (error) {
      toast.error('Failed to cancel auto-contribution');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getFrequencyLabel = (freq) => {
    const labels = {
      daily: 'Daily',
      weekly: 'Weekly',
      biweekly: 'Bi-weekly',
      monthly: 'Monthly',
      yearly: 'Yearly'
    };
    return labels[freq] || freq;
  };

  // Find schedule for current club
  const currentClubSchedule = clubId ? schedules.find(s => s.club_id === clubId) : null;

  if (loading) {
    return (
      <Card className="border-stone-200">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-stone-200" data-testid="auto-contribution-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <CalendarClock className="w-5 h-5 mr-2 text-indigo-600" />
                Auto-Contributions
              </CardTitle>
              <CardDescription>
                Set up automatic contributions from your Mini Bank
              </CardDescription>
            </div>
            
            {clubId && !currentClubSchedule && (
              <Button
                onClick={() => setSetupDialog(true)}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="setup-auto-contribution-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Set Up
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent>
          {schedules.length === 0 ? (
            <div className="text-center py-8">
              <CalendarClock className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500 mb-2">No auto-contributions set up</p>
              <p className="text-sm text-stone-400">
                Set up automatic contributions to never miss a payment
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {schedules.map((schedule) => (
                <motion.div
                  key={schedule.schedule_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-xl border ${
                    schedule.is_active 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-stone-50 border-stone-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                        schedule.is_active ? 'bg-green-100' : 'bg-stone-200'
                      }`}>
                        {schedule.is_active ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Pause className="w-5 h-5 text-stone-500" />
                        )}
                      </div>
                      <div>
                        <div className="font-semibold text-stone-900">
                          {schedule.club_name}
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {getFrequencyLabel(schedule.frequency)}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggle(schedule.schedule_id, schedule.is_active)}
                        data-testid={`toggle-${schedule.schedule_id}`}
                      >
                        {schedule.is_active ? (
                          <Pause className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(schedule.schedule_id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-${schedule.schedule_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-stone-500">Amount</div>
                      <div className="font-semibold text-stone-900 flex items-center">
                        <DollarSign className="w-4 h-4 mr-1" />
                        {schedule.amount.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-stone-500">Next Contribution</div>
                      <div className="font-semibold text-stone-900 flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate(schedule.next_contribution_date)}
                      </div>
                    </div>
                  </div>

                  {schedule.retry_count > 0 && (
                    <div className="mt-3 p-2 bg-amber-100 rounded-lg flex items-center text-amber-800 text-sm">
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Retrying due to insufficient balance ({schedule.retry_count} attempts)
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Setup Dialog */}
      <Dialog open={setupDialog} onOpenChange={setSetupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <CalendarClock className="w-5 h-5 mr-2 text-indigo-600" />
              Set Up Auto-Contribution
            </DialogTitle>
            <DialogDescription>
              Configure automatic contributions for {clubName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Use Club Frequency Toggle */}
            <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
              <div>
                <div className="font-medium text-stone-900">Use Club Schedule</div>
                <div className="text-sm text-stone-500">
                  Align with the club's {getFrequencyLabel(clubFrequency || 'weekly').toLowerCase()} payout schedule
                </div>
              </div>
              <Switch
                checked={useClubFrequency}
                onCheckedChange={setUseClubFrequency}
                data-testid="use-club-frequency-switch"
              />
            </div>

            {/* Custom Options */}
            <AnimatePresence>
              {!useClubFrequency && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <Label>Custom Amount</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                      <Input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        placeholder={clubAmount?.toString() || '0.00'}
                        className="pl-9"
                        data-testid="custom-amount-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select value={customFrequency} onValueChange={setCustomFrequency}>
                      <SelectTrigger data-testid="frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Bi-weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {customFrequency === 'weekly' && (
                    <div className="space-y-2">
                      <Label>Preferred Day</Label>
                      <Select value={customDay} onValueChange={setCustomDay}>
                        <SelectTrigger>
                          <SelectValue placeholder="Any day" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monday">Monday</SelectItem>
                          <SelectItem value="tuesday">Tuesday</SelectItem>
                          <SelectItem value="wednesday">Wednesday</SelectItem>
                          <SelectItem value="thursday">Thursday</SelectItem>
                          <SelectItem value="friday">Friday</SelectItem>
                          <SelectItem value="saturday">Saturday</SelectItem>
                          <SelectItem value="sunday">Sunday</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {customFrequency === 'monthly' && (
                    <div className="space-y-2">
                      <Label>Day of Month</Label>
                      <Input
                        type="number"
                        min="1"
                        max="28"
                        value={customDay}
                        onChange={(e) => setCustomDay(e.target.value)}
                        placeholder="1-28"
                        data-testid="day-of-month-input"
                      />
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Summary */}
            <div className="p-4 bg-indigo-50 rounded-lg">
              <div className="text-sm text-indigo-900">
                <strong>Summary:</strong> Auto-contribute{' '}
                <span className="font-semibold">
                  ${useClubFrequency ? clubAmount : (parseFloat(customAmount) || clubAmount)}
                </span>{' '}
                {useClubFrequency ? getFrequencyLabel(clubFrequency || 'weekly').toLowerCase() : getFrequencyLabel(customFrequency).toLowerCase()}
                {' '}from your Mini Bank
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSetupDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSetup}
              disabled={processing}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="confirm-setup-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Setting up...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Set Up Auto-Contribution
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
